/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ChangeEvent, useEffect, useState } from "react";
import { ApolloError } from "@apollo/client";
import { When } from "react-if";
import { useDebounce } from "react-use";
import {
  Input,
  Sidebar,
  Box,
  theme,
  FlexContainer,
  Table,
  Spacer,
} from "@nordcloud/gnui";
import { UniversalWrap } from "~/components/Utils";
import { useVpcSubnets } from "./hooks";
import { Vpc } from "./types";
import { generateSelectedSubnets } from "./utils";
import { VpcSubnetRow } from "./VpcSubnetRow";
import { SidebarFooter } from "./VpcSubnetSidebarFooter";

type Props = {
  loading: boolean;
  error: ApolloError | undefined;
  isOpen: boolean;
  vpcs: Vpc[];
  initialSubnets: string[];
  page: number;
  pageSize: number;
  vpcsCount: number;
  onClose: () => void;
  onSubmit: (value: string) => void;
  onDelete: (subnetIds: string[]) => void;
  setPage: (page: number) => void;
  setVpcSearch: (input: string) => void;
};

const DEBOUNCE_TIMEOUT_MS = 500;

export function VpcSubnetSidebar({
  loading,
  error,
  isOpen,
  vpcs,
  initialSubnets,
  page,
  pageSize,
  vpcsCount,
  onClose,
  onSubmit,
  onDelete,
  setPage,
  setVpcSearch,
}: Props) {
  const {
    selectedSubnetIds,
    selectedSubnets,
    setSelectedSubnets,
    removedSubnets,
    isDisabled,
  } = useVpcSubnets(initialSubnets, vpcs);
  const [search, setSearch] = useState("");

  useDebounce(
    () => {
      if (search.length !== 1) {
        setPage(0);
        setVpcSearch(search);
      }
    },
    DEBOUNCE_TIMEOUT_MS,
    [search]
  );

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value.toLowerCase());
  };

  const handleClose = () => {
    onClose();
    setPage(0);
    setVpcSearch("");
  };

  const onSubmitHandle = () => {
    selectedSubnetIds.forEach((subnet) => {
      if (!initialSubnets.includes(subnet)) {
        onSubmit(subnet);
      }
    });
    onDelete(removedSubnets);
    handleClose();
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedSubnets((prevSelectedSubnets) =>
        generateSelectedSubnets(
          prevSelectedSubnets.map((subnet) => subnet.subnetId),
          vpcs
        )
      );
    } else {
      setSelectedSubnets(generateSelectedSubnets(initialSubnets, vpcs));
    }
  }, [isOpen, vpcs, initialSubnets, setSelectedSubnets]);

  return (
    <Sidebar
      title="Select VPCs or Subnets"
      isOpen={isOpen}
      headerStyles={{ zIndex: 2 }}
      footer={
        <When condition={!loading}>
          <SidebarFooter
            page={page}
            pageSize={pageSize}
            count={vpcsCount}
            setPage={setPage}
            isDisabled={isDisabled}
            onSubmitHandle={onSubmitHandle}
          />
        </When>
      }
      onClick={handleClose}
    >
      <Box boxStyle="lightGrey">
        <FlexContainer>
          <div css={{ flexGrow: 1 }}>
            <Input
              small
              type="text"
              placeholder="Search"
              onChange={onSearchChange}
            />
          </div>
        </FlexContainer>
      </Box>
      <Spacer height={theme.spacing.spacing04} />
      <UniversalWrap
        errorProps={{ error }}
        loaderProps={{
          loading: isOpen && loading,
          inContent: true,
        }}
      >
        <Table small>
          {vpcs.map((vpc) => (
            <VpcSubnetRow
              key={vpc.nid}
              name={vpc.vpcId}
              subnets={vpc?.subnets ?? []}
              selectedSubnets={selectedSubnets}
              setSelectedSubnets={setSelectedSubnets}
              disabled={false}
              onSubmit={onSubmit}
            />
          ))}
        </Table>
      </UniversalWrap>
    </Sidebar>
  );
}
